import _ from 'lodash';
import * as types from '../mutation-types';
import questions from '../../api/questions';
import products from '../../api/products';

const state = {
  questions: [
    {
      id: null,
      number: null,
      text: null
    }
  ],
  selected: {
    id: null,
    number: null,
    text: null,
    journeyType: {
      name: null
    },
    questionType: {
      name: null
    },
    answers: [
      {
        id: null
      }
    ]
  },
  selectedAnswer: {
    id: null,
    question_id: null,
    value: null,
    bbb_value: null,
    label: null,
    enabled: null,
    warning_message: null
  },
  questionTypes: [
    {
      id: null,
      name: null,
      label: null
    }
  ],
  products: [
    {
      id: null,
      name: null
    }
  ]
};

const getters = {
  Questions: state => state.questions,
  GetAnswersForQuestion: state => id => _.find(state.questions, question => question.id === id).answers,
  SelectedQuestion: state => state.selected,
  QuestionTypes: state => state.questionTypes,
  QuestionProducts: state => state.products,
  SelectedAnswer: state => state.selectedAnswer,
  getQuestionById: state => id => _.find(state.questions, question => question.id === id),
  getAnswerLabelById: state => (questionId, answerId) => {
    let question = _.find(state.questions, question => question.id === questionId);
    return _.find(question.answers, answer => answer.id === answerId).label;
  }
};

const actions = {
  getQuestions({ commit, dispatch }) {
    questions.getQuestions().then(questions => {
      commit(types.SET_QUESTIONS, questions.data);
      dispatch('selectStoredQuestion', questions.data[0].id);
    });
  },
  selectQuestion({ commit }, { id }) {
    questions.getQuestion(id).then(question => {
      commit(types.SET_SELECTED_QUESTION, question.data);
      const answers = _.orderBy(question.data.answers, ['value'], ['asc']);
      commit(types.SET_SELECTED_ANSWER, answers[0]);
    });
  },
  selectAnswer({ commit }, answer) {
    commit(types.SET_SELECTED_ANSWER, answer);
  },
  selectStoredQuestion({ commit }, id) {
    const question = _.find(state.questions, question => question.id === id);
    commit(types.SET_SELECTED_QUESTION, question);
    const answers = _.orderBy(question.answers, ['value'], ['asc']);
    commit(types.SET_SELECTED_ANSWER, answers[0]);
  },
  unsetSelectQuestion({ commit }) {
    commit(types.UNSET_SELECTED_QUESTION);
  },
  updateQuestion({ commit, dispatch }, { id, updates }) {
    questions.update(id, updates).then(question => {
      dispatch('addNotification', {
        text: 'Question details have been updated',
        bottom: true,
        left: true,
        colour: 'success'
      });
      commit(types.SET_SELECTED_QUESTION, question.data);
    });
  },
  addQuestion({ dispatch }, question) {
    questions.addQuestion(question).then(() => {
      dispatch('getJourneyTypes');
      dispatch('addNotification', {
        text: 'New Question has been added',
        bottom: true,
        left: true,
        colour: 'success'
      });
    });
  },
  updateAnswer({ commit, dispatch }, { id, updates }) {
    questions.updateAnswer(id, updates).then(answer => {
      commit(types.UPDATE_ANSWER, answer.data);
      dispatch('addNotification', {
        text: 'Answer details have been updated',
        bottom: true,
        left: true,
        colour: 'success'
      });
    });
  },
  addAnswer({ dispatch }, { answer }) {
    questions.addAnswer(answer).then(() => {
      dispatch('addNotification', {
        text: 'New Answer has been added',
        bottom: true,
        left: true,
        colour: 'success'
      });
      dispatch('selectQuestion', { id: answer.question_id });
    });
  },
  reOrderQuestions({ commit }, { newOrder, groupId }) {
    commit(types.UPDATE_QUESTION_ORDER, newOrder);
    questions.reOrder(newOrder, groupId);
  },
  getQuestionTypes({ commit }) {
    questions.getQuestionTypes().then(questionTypes => {
      commit(types.SET_QUESTION_TYPES, questionTypes.data);
    });
  },
  getQuestionProducts({ commit }) {
    products.getProductsList().then(products => {
      commit(types.SET_QUESTION_PRODUCT_LIST, products.data);
    });
  },
  attachQuestionParent({ dispatch }, { id, answerId }) {
    questions.attachParent(id, answerId).then(() => {
      dispatch('selectQuestion', { id });
    });
  },
  detachQuestionParent({ dispatch }, { id, answerId }) {
    questions.detachParent(id, answerId).then(() => {
      dispatch('selectQuestion', { id });
    });
  },
  attachProduct({ dispatch }, { id, productId }) {
    questions.attachProduct(id, productId).then(() => {
      dispatch('selectQuestion', { id });
    });
  },
  detachProduct({ dispatch }, { id, productId }) {
    questions.detachProduct(id, productId).then(() => {
      dispatch('selectQuestion', { id });
    });
  },
  attachGroup({ dispatch }, { id, groupId }) {
    questions.attachGroup(id, groupId).then(() => {
      dispatch('selectQuestion', { id });
    });
  },
  detachGroup({ dispatch }, { id, groupId }) {
    questions.detachGroup(id, groupId).then(() => {
      dispatch('selectQuestion', { id });
    });
  },
  attachApiProduct({ dispatch }, { id, productId }) {
    questions.attachApiProduct(id, productId).then(() => {
      dispatch('selectQuestion', { id });
    });
  },
  detachApiProduct({ dispatch }, { id, productId }) {
    questions.detachApiProduct(id, productId).then(() => {
      dispatch('selectQuestion', { id });
    });
  },
  setSelectedAnswerQuestionId({ commit }, id) {
    commit(types.SET_SELECTED_ANSWER_QUESTION_ID, id);
  },
  setSelectedAnswerValue({ commit }, value) {
    commit(types.SET_SELECTED_ANSWER_VALUE, value);
  },
  setSelectedAnswerLabel({ commit }, label) {
    commit(types.SET_SELECTED_ANSWER_LABEL, label);
  },
  setSelectedAnswerBBBValue({ commit }, bbbValue) {
    commit(types.SET_SELECTED_ANSWER_BBB_VALUE, bbbValue);
  },
  setSelectedAnswerEnabled({ commit }, enabled) {
    commit(types.SET_SELECTED_ANSWER_ENABLED, enabled);
  },
  setSelectedAnswerWarningMessage({ commit }, warningMessage) {
    commit(types.SET_SELECTED_ANSWER_WARNING_MESSAGE, warningMessage);
  }
};

const mutations = {
  [types.SET_QUESTIONS](state, questions) {
    state.questions = questions;
  },
  [types.SET_SELECTED_QUESTION](state, question) {
    state.selected = question;
    const index = _.findIndex(state.questions, ['id', question.id]);
    if (index >= 0) {
      _.merge(state.questions[index], question);
    }
  },
  [types.SET_SELECTED_ANSWER](state, answer) {
    state.selectedAnswer = answer;
  },
  [types.UNSET_SELECTED_QUESTION](state) {
    state.selected = {
      id: null,
      number: null,
      text: null,
      journeyType: {
        name: null
      },
      questionType: {
        name: null
      },
      answers: [
        {
          id: null
        }
      ]
    };
  },
  [types.SET_QUESTION_TYPES](state, questionTypes) {
    state.questionTypes = questionTypes;
  },
  [types.SET_QUESTION_PRODUCT_LIST](state, products) {
    state.products = products;
  },
  [types.UPDATE_ANSWER](state, answer) {
    let answerIndex = _.findIndex(state.selected.answers, ['id', answer.id]);
    if (answerIndex >= 0) {
      state.selected.answers[answerIndex] = answer;
    }
    const questionIndex = _.findIndex(state.questions, ['id', answer.question_id]);
    if (questionIndex >= 0) {
      answerIndex = _.findIndex(state.questions[questionIndex].answers, ['id', answer.id]);
      if (answerIndex >= 0) {
        state.questions[questionIndex].answers[answerIndex] = answer;
      }
    }
  },
  [types.SET_SELECTED_ANSWER_QUESTION_ID](state, id) {
    state.selectedAnswer.question_id = id;
  },
  [types.SET_SELECTED_ANSWER_VALUE](state, value) {
    state.selectedAnswer.value = value;
  },
  [types.SET_SELECTED_ANSWER_LABEL](state, label) {
    state.selectedAnswer.label = label;
  },
  [types.SET_SELECTED_ANSWER_BBB_VALUE](state, bbb_value) {
    state.selectedAnswer.bbb_value = bbb_value;
  },
  [types.SET_SELECTED_ANSWER_ENABLED](state, enabled) {
    state.selectedAnswer.enabled = enabled;
  },
  [types.SET_SELECTED_ANSWER_WARNING_MESSAGE](state, warning_message) {
    state.selectedAnswer.warning_message = warning_message;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
